import React, { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BackgroundAnalysisStatus } from '../types';
import { formatTime } from '../services/backgroundAnalysisService';
import AnalysisResultSkeleton from './AnalysisResultSkeleton';

const LOADER_PHRASE_KEYS = [
  'loaderPhrase0', 'loaderPhrase1', 'loaderPhrase2', 'loaderPhrase3', 'loaderPhrase4',
  'loaderPhrase5', 'loaderPhrase6', 'loaderPhrase7', 'loaderPhrase8', 'loaderPhrase9',
];

interface FullScreenLoaderProps {
  backgroundAnalysisStatus: BackgroundAnalysisStatus | null | undefined;
  isLoading?: boolean;
  theme?: 'dark' | 'light';
}

const FullScreenLoader: React.FC<FullScreenLoaderProps> = ({ 
  backgroundAnalysisStatus, 
  isLoading = false,
  theme = 'light'
}) => {
  const { t } = useTranslation('analysis');
  // Храним максимальное значение прогресса, чтобы он не откатывался назад
  const maxProgressRef = useRef<number>(0);
  // Храним предыдущий taskId для отслеживания смены задачи
  const previousTaskIdRef = useRef<string | null>(null);
  // Индекс текущей фразы для отображения
  const [currentPhraseIndex, setCurrentPhraseIndex] = useState<number>(0);
  // Флаг, что таймер запущен
  const timerStartedRef = useRef<boolean>(false);
  // ID таймера для очистки
  const timerIdRef = useRef<NodeJS.Timeout | null>(null);
  // Сохраняем начальное время анализа для расчета интервалов
  const initialAnalysisTimeRef = useRef<number | null>(null);

  // Показываем прелоадер только если идет анализ
  const shouldShow = backgroundAnalysisStatus && 
    backgroundAnalysisStatus.status !== 'completed' && 
    backgroundAnalysisStatus.status !== 'failed';

  // Сбрасываем максимальный прогресс и таймер при сбросе статуса или смене задачи
  useEffect(() => {
    const currentTaskId = backgroundAnalysisStatus?.taskId || null;
    
    // Если статус сброшен или сменилась задача, сбрасываем все
    if (!backgroundAnalysisStatus || currentTaskId !== previousTaskIdRef.current) {
      maxProgressRef.current = 0;
      setCurrentPhraseIndex(0);
      timerStartedRef.current = false;
      initialAnalysisTimeRef.current = null; // Сбрасываем сохраненное время
      
      // Очищаем таймер, если он был запущен
      if (timerIdRef.current) {
        clearInterval(timerIdRef.current);
        timerIdRef.current = null;
      }
      
      // Обновляем previousTaskIdRef после очистки
      previousTaskIdRef.current = currentTaskId;
    }
  }, [backgroundAnalysisStatus]);

  // Запускаем таймер для переключения фраз при получении времени анализа
  useEffect(() => {
    // Не запускаем, если нет статуса или taskId
    if (
      !backgroundAnalysisStatus ||
      !backgroundAnalysisStatus.taskId
    ) {
      return;
    }

    const currentTaskId = backgroundAnalysisStatus.taskId;
    
    // Проверяем, не запущен ли уже таймер для этой задачи
    // Если таймер уже запущен для этой задачи, просто выходим
    if (timerStartedRef.current && previousTaskIdRef.current === currentTaskId) {
      // Таймер уже работает, не перезапускаем
      return;
    }

    // Получаем время анализа (приоритет: averageAnalysisTime, затем estimatedTimeRemaining)
    // Время уже в миллисекундах
    // Используем averageAnalysisTime, если есть, иначе сохраняем начальное estimatedTimeRemaining
    let analysisTime: number | null = null;
    
    if (backgroundAnalysisStatus.averageAnalysisTime) {
      // Используем среднее время анализа (оно постоянно)
      analysisTime = backgroundAnalysisStatus.averageAnalysisTime;
      initialAnalysisTimeRef.current = analysisTime;
    } else if (backgroundAnalysisStatus.estimatedTimeRemaining) {
      // Если среднего времени нет, используем оставшееся время
      // Но сохраняем его начальное значение, если еще не сохранили
      if (initialAnalysisTimeRef.current === null) {
        // Первый раз получаем estimatedTimeRemaining - сохраняем его
        // Но нужно учесть, что это уже оставшееся время, а не полное
        // Используем его как есть, но это не идеально
        analysisTime = backgroundAnalysisStatus.estimatedTimeRemaining;
        initialAnalysisTimeRef.current = analysisTime;
      } else {
        // Используем сохраненное начальное время
        analysisTime = initialAnalysisTimeRef.current;
      }
    }

    // Если время не получено, не запускаем таймер
    if (!analysisTime || analysisTime <= 0) {
      return;
    }

    // Очищаем предыдущий таймер, если он был
    if (timerIdRef.current) {
      clearInterval(timerIdRef.current);
      timerIdRef.current = null;
    }

    // Запускаем таймер только один раз для этой задачи
    timerStartedRef.current = true;
    previousTaskIdRef.current = currentTaskId;

    // Вычисляем интервал между переключениями фраз
    // У нас 10 фраз, значит нужно 9 переключений (от 0 до 9)
    // Разделяем общее время анализа на (количество фраз - 1) интервалов
    const phraseCount = LOADER_PHRASE_KEYS.length;
    const interval = Math.max(100, analysisTime / (phraseCount - 1)); // Минимум 100мс между переключениями

    // Переключаем фразы по очереди, используя функциональное обновление состояния
    timerIdRef.current = setInterval(() => {
      setCurrentPhraseIndex((prevIndex) => {
        const nextIndex = prevIndex + 1;
        if (nextIndex >= phraseCount) {
          // Достигли последней фразы, останавливаем таймер
          if (timerIdRef.current) {
            clearInterval(timerIdRef.current);
            timerIdRef.current = null;
          }
          return phraseCount - 1; // Остаемся на последней фразе
        }
        return nextIndex;
      });
    }, interval);

    // Очистка таймера только при размонтировании компонента или смене задачи
    // НЕ очищаем таймер при обновлении статуса, чтобы он продолжал работать
    // Сохраняем taskId в замыкании для проверки
    const savedTaskId = currentTaskId;
    return () => {
      // Проверяем, не сменилась ли задача после создания эффекта
      // Если задача не сменилась, НЕ очищаем таймер
      if (backgroundAnalysisStatus?.taskId !== savedTaskId) {
        if (timerIdRef.current) {
          clearInterval(timerIdRef.current);
          timerIdRef.current = null;
        }
      }
    };
  }, [backgroundAnalysisStatus]); // Зависим от всего статуса, чтобы проверять время при обновлении

  if (!shouldShow && !isLoading) {
    return null;
  }

  // Рассчитываем текущий прогресс
  const currentProgress = backgroundAnalysisStatus?.timeSinceStart && backgroundAnalysisStatus?.averageAnalysisTime
    ? Math.min(100, (backgroundAnalysisStatus.timeSinceStart / backgroundAnalysisStatus.averageAnalysisTime) * 100)
    : 0;

  // Обновляем максимальный прогресс, чтобы он никогда не уменьшался
  if (currentProgress > maxProgressRef.current) {
    maxProgressRef.current = currentProgress;
  }

  // Используем максимальный прогресс для отображения
  const progress = maxProgressRef.current;
  const remainingTimeText = backgroundAnalysisStatus?.estimatedTimeRemaining !== null &&
    backgroundAnalysisStatus?.estimatedTimeRemaining !== undefined &&
    backgroundAnalysisStatus.estimatedTimeRemaining > 0
    ? t('loaderRemaining', { time: formatTime(backgroundAnalysisStatus.estimatedTimeRemaining) })
    : null;

  // Получаем текущую фразу на основе индекса (перевод по ключу)
  const currentPhrase = t(LOADER_PHRASE_KEYS[Math.min(currentPhraseIndex, LOADER_PHRASE_KEYS.length - 1)]);

  // Проверяем, есть ли время для расчета интервалов
  const hasAnalysisTime = backgroundAnalysisStatus?.averageAnalysisTime || 
                         backgroundAnalysisStatus?.estimatedTimeRemaining;

  // Определяем текст для отображения
  const statusText = (() => {
    // Если нет времени, показываем дефолтный текст в зависимости от статуса
    if (!hasAnalysisTime) {
      if (backgroundAnalysisStatus?.status === 'pending') {
        return t('loaderQueueWaiting');
      }
      // Для других статусов без времени показываем первую фразу
      return t(LOADER_PHRASE_KEYS[0]);
    }
    // Если время есть, показываем текущую фразу (таймер работает)
    return currentPhrase;
  })();

  // Контент для центра (логотип + прогресс-бар)
  const loaderContent = (
    <div key="loader-narrow-v2" className="flex flex-col items-center space-y-4 w-full" style={{ maxWidth: '500px' }}>
      {/* Анимированный логотип */}
      <div className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center">
        <img 
          src="/images/animatedrefined4-animated.svg" 
          alt="ПЛИНУС" 
          className="w-full h-full object-contain"
        />
      </div>

      {/* Текст статуса */}
      <div className="text-center flex items-center justify-center">
        <div className={`text-xl sm:text-2xl font-semibold transition-all duration-500 ${
          theme === 'dark' ? 'text-white' : 'text-gray-900'
        }`}>
          {statusText}
        </div>
      </div>

      {/* Прогресс-бар */}
      <div className="w-full space-y-1">
        <div className={`w-full rounded-full h-3 sm:h-4 overflow-hidden shadow-inner relative ${
          theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
        }`}>
          <div 
            className="bg-gradient-to-r from-blue-500 via-purple-500 to-purple-600 h-full transition-all duration-300 ease-out rounded-full relative overflow-hidden"
            style={{ width: `${progress}%` }}
          >
            {/* Анимация блеска на прогресс-баре - светлая полоса, проходящая слева направо */}
            <div 
              key="shimmer-bright"
              className="absolute inset-0"
              style={{
                background: 'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.25) 40%, rgba(255, 255, 255, 0.25) 60%, transparent 100%)',
                width: '50%',
                height: '100%',
                animation: 'shimmer-slow 5s ease-in-out infinite'
              }}
            />
          </div>
        </div>
        {/* Место под таймер с фиксированной высотой, чтобы избежать скачков */}
        <div className="h-4 sm:h-5 flex items-center justify-center">
          {remainingTimeText ? (
            <div className={`text-sm sm:text-base text-center font-medium animate-in fade-in duration-300 ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              {remainingTimeText}
            </div>
          ) : (
            <div className="text-sm sm:text-base opacity-0 select-none" aria-hidden="true">
              {t('loaderRemainingPlaceholder')}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[9999]">
      {/* Скелетон AnalysisResult с логотипом и прогресс-баром внутри */}
      <div className={`absolute inset-0 overflow-hidden ${
        theme === 'dark' 
          ? 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900' 
          : 'bg-white'
      }`}>
        <AnalysisResultSkeleton centerContent={loaderContent} theme={theme} />
        {/* Полупрозрачная вуаль поверх скелетона */}
        <div className={`absolute inset-0 ${
          theme === 'dark' 
            ? 'bg-gradient-to-br from-slate-900/70 via-slate-800/70 to-slate-900/70' 
            : 'bg-white/70'
        }`} />
      </div>
      
      <style>{`
        @keyframes shimmer-slow {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(400%);
          }
        }
      `}</style>
    </div>
  );
};

export default FullScreenLoader;

